// Matrix auth: password login → access token persisted in SecureStore → /sync loop.
import React, { createContext, useContext, useEffect, useState } from 'react';
import { Platform } from 'react-native';
import * as SecureStore from 'expo-secure-store';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { login as mxLogin, requestEmailCode, loginWithEmailCode, whoami, serverLogout, setSession, startSync, stopSync, setOnAuthLost, Session } from './matrix';

const KEY = 'mx_session';
type Phase = 'loading' | 'signedOut' | 'signedIn';

// SecureStore has no web implementation; the browser (test runs only) falls back to localStorage.
const store = Platform.OS === 'web'
  ? { getItemAsync: (k: string) => AsyncStorage.getItem(k), setItemAsync: (k: string, v: string) => AsyncStorage.setItem(k, v), deleteItemAsync: (k: string) => AsyncStorage.removeItem(k) }
  : { getItemAsync: (k: string) => SecureStore.getItemAsync(k), setItemAsync: (k: string, v: string) => SecureStore.setItemAsync(k, v), deleteItemAsync: (k: string) => SecureStore.deleteItemAsync(k) };

type AuthCtx = {
  phase: Phase;
  userId: string | null;
  error: string | null;
  busy: boolean;
  login: (user: string, password: string) => Promise<boolean>;
  requestCode: (email: string) => Promise<boolean>;            // emailed sign-in code, step 1
  loginWithCode: (email: string, code: string) => Promise<boolean>; // step 2
  logout: () => Promise<void>;
  clearError: () => void;
};

const Ctx = createContext<AuthCtx>(null as any);
export const useAuth = () => useContext(Ctx);

function friendlyCode(e: any): string {
  if (e?.errcode === 'M_FORBIDDEN') return 'That code did not work. Check it, or ask for a new one.';
  if (e?.errcode === 'M_LIMIT_EXCEEDED') return 'Too many tries — wait a minute, then ask for a new code.';
  if (e?.errcode === 'M_UNRECOGNIZED' || e?.errcode === 'M_UNKNOWN' || e?.status === 400) return 'This server does not offer email sign-in yet. Use your password instead.';
  return friendly(e);
}

function friendly(e: any): string {
  if (e?.errcode === 'M_FORBIDDEN') return 'Wrong username or password.';
  if (e?.errcode === 'M_LIMIT_EXCEEDED') return 'Too many attempts — wait a few minutes and try again.';
  if (e?.errcode === 'M_USER_DEACTIVATED') return 'This account has been deactivated.';
  return e?.message || 'Sign-in failed. Check your connection and try again.';
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [phase, setPhase] = useState<Phase>('loading');
  const [userId, setUserId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const signOutLocal = async () => {
    stopSync();
    setSession(null);
    await store.deleteItemAsync(KEY).catch(() => {});
    setUserId(null);
    setPhase('signedOut');
  };

  // Restore the session on launch; a rejected token drops back to sign-in.
  useEffect(() => {
    setOnAuthLost(() => { signOutLocal(); });
    (async () => {
      try {
        const raw = await store.getItemAsync(KEY);
        if (raw) {
          const s: Session = JSON.parse(raw);
          setSession(s);
          await whoami();
          setUserId(s.userId);
          setPhase('signedIn');
          startSync();
          return;
        }
      } catch {
        setSession(null);
        await store.deleteItemAsync(KEY).catch(() => {});
      }
      setPhase('signedOut');
    })();
    return () => setOnAuthLost(null);
  }, []);

  const login = async (user: string, password: string) => {
    if (busy) return false; // ignore repeat taps while a login is in flight (avoids tripping the server rate limit)
    setBusy(true); setError(null);
    try {
      const s = await mxLogin(user, password);
      setSession(s);
      await store.setItemAsync(KEY, JSON.stringify(s));
      setUserId(s.userId);
      setPhase('signedIn');
      startSync();
      return true;
    } catch (e: any) {
      setError(friendly(e));
      return false;
    } finally { setBusy(false); }
  };

  // Emailed code, step 1: the server answers the same whether or not the address is known.
  const requestCode = async (email: string) => {
    if (busy) return false;
    const addr = email.trim();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(addr)) { setError('Enter the email address you signed up with.'); return false; }
    setBusy(true); setError(null);
    try { await requestEmailCode(addr); return true; }
    catch (e: any) { setError(e?.status === 404 ? 'This server does not offer email sign-in yet. Use your password instead.' : friendly(e)); return false; }
    finally { setBusy(false); }
  };

  // Emailed code, step 2: same session handling as a password login.
  const loginWithCode = async (email: string, code: string) => {
    if (busy) return false;
    setBusy(true); setError(null);
    try {
      const s = await loginWithEmailCode(email, code);
      setSession(s);
      await store.setItemAsync(KEY, JSON.stringify(s));
      setUserId(s.userId);
      setPhase('signedIn');
      startSync();
      return true;
    } catch (e: any) {
      setError(friendlyCode(e));
      return false;
    } finally { setBusy(false); }
  };

  const logout = async () => {
    serverLogout(); // best-effort; reads the token synchronously before we clear it
    await signOutLocal();
  };

  return (
    <Ctx.Provider value={{ phase, userId, error, busy, login, requestCode, loginWithCode, logout, clearError: () => setError(null) }}>
      {children}
    </Ctx.Provider>
  );
}
